/** Google Website Translator — traduce tutto il DOM (non solo le chiavi i18n). */

export const GOOGTRANS_COOKIE = "googtrans";
export const PAGE_LANG = "it";

export function readGoogTransTarget() {
  try {
    const raw = document.cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${GOOGTRANS_COOKIE}=`));
    if (!raw) return null;
    const val = decodeURIComponent(raw.split("=").slice(1).join("="));
    const parts = val.split("/").filter(Boolean);
    return parts.length >= 2 ? parts[parts.length - 1] : null;
  } catch {
    return null;
  }
}

function setCookie(name, value, { domain } = {}) {
  const base = `${name}=${encodeURIComponent(value)}; path=/; max-age=31536000; SameSite=Lax`;
  document.cookie = base;
  if (domain) document.cookie = `${base}; domain=${domain}`;
}

function clearCookie(name) {
  const expire = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
  document.cookie = expire;
  try {
    const host = window.location.hostname;
    if (host && host.includes(".")) {
      document.cookie = `${expire}; domain=${host}`;
      document.cookie = `${expire}; domain=.${host}`;
    }
  } catch {
    /* ignore */
  }
}

/** Imposta cookie googtrans per lingua target (o azzera se IT). */
export function writeGoogTransCookie(lang) {
  const code = (lang || PAGE_LANG).split("-")[0].toLowerCase();
  if (!code || code === PAGE_LANG) {
    clearCookie(GOOGTRANS_COOKIE);
    return null;
  }
  const value = `/${PAGE_LANG}/${code}`;
  setCookie(GOOGTRANS_COOKIE, value);
  try {
    const host = window.location.hostname;
    if (host && host.includes(".")) setCookie(GOOGTRANS_COOKIE, value, { domain: host });
  } catch {
    /* ignore */
  }
  return value;
}

/** Attiva/disattiva traduzione Google sul select nascosto.
 *  `force` riapplica la traduzione anche se il combo è già sulla lingua:
 *  dopo navigazione SPA React rigenera il DOM in italiano mentre il widget
 *  resta fermo sul target — senza forzatura la pagina nuova resta italiana. */
export function applyGoogleTranslate(lang, { force = false } = {}) {
  if (typeof document === "undefined") return false;
  const code = (lang || PAGE_LANG).split("-")[0].toLowerCase();
  writeGoogTransCookie(code);

  const select = document.querySelector("select.goog-te-combo");
  if (!select) return false;

  if (code === PAGE_LANG) {
    // Torna all'originale: valore vuoto o "it"
    if (select.value && select.value !== PAGE_LANG && select.value !== "") {
      select.value = PAGE_LANG;
      select.dispatchEvent(new Event("change"));
    }
    // Se ancora tradotto, forza restore via cookie + combo
    const restore = document.querySelector("#\\:1\\.restore") || document.querySelector(".goog-te-balloon-frame");
    if (document.documentElement.classList.contains("translated-ltr") || document.body?.className?.includes("translated")) {
      select.value = "";
      select.dispatchEvent(new Event("change"));
    }
    return true;
  }

  if (force && select.value === code) {
    // Il widget ignora un change sullo stesso valore: ripristina l'originale
    // e riapplica, così i nodi DOM nuovi (cambio rotta SPA) vengono tradotti.
    select.value = "";
    select.dispatchEvent(new Event("change"));
  }
  if (select.value !== code) {
    select.value = code;
    select.dispatchEvent(new Event("change"));
  }
  return true;
}

/**
 * Trigger lingua: cookie googtrans + combo Google.
 * Se la combo non è ancora nel DOM, salva la lingua pendente e ricarica:
 * al reload il cookie googtrans fa partire la traduzione automaticamente.
 * Il flag sessionStorage evita loop di reload se lo script è bloccato.
 */
const PENDING_LANG_KEY = "googtrans_pending_lang";

export function triggerLanguageChange(lang) {
  const targetLang = (lang || PAGE_LANG).split("-")[0].toLowerCase();
  writeGoogTransCookie(targetLang);

  const select = document.querySelector("select.goog-te-combo");
  if (select) {
    const ok = applyGoogleTranslate(targetLang);
    if (ok) {
      try {
        sessionStorage.removeItem(PENDING_LANG_KEY);
      } catch {
        /* ignore */
      }
    }
    return ok;
  }

  if (targetLang !== PAGE_LANG) {
    try {
      if (sessionStorage.getItem(PENDING_LANG_KEY) !== targetLang) {
        sessionStorage.setItem(PENDING_LANG_KEY, targetLang);
        window.location.reload();
      }
    } catch {
      /* storage non disponibile: niente reload forzato */
    }
  }
  return false;
}

/** Lingua pendente richiesta prima dell'ultimo reload (se presente). */
export function consumePendingLang() {
  try {
    const v = sessionStorage.getItem(PENDING_LANG_KEY);
    sessionStorage.removeItem(PENDING_LANG_KEY);
    return v;
  } catch {
    return null;
  }
}

export function loadGoogleTranslateScript() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("no window"));
      return;
    }
    if (window.google?.translate?.TranslateElement) {
      resolve(window.google.translate);
      return;
    }
    const existing = document.getElementById("google-translate-script");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google?.translate));
      return;
    }

    window.googleTranslateElementInit = () => {
      try {
        // eslint-disable-next-line no-new
        new window.google.translate.TranslateElement(
          {
            pageLanguage: PAGE_LANG,
            includedLanguages: "it,en,fr,de,es,pt,nl,pl,hr,el,ro,cs,sk,hu,bg,da,sv,fi,et,lv,lt,sl,mt,ga",
            autoDisplay: false,
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
          },
          "google_translate_element",
        );
      } catch (e) {
        console.warn("[i18n] Google Translate init failed", e);
      }
      resolve(window.google?.translate);
    };

    const script = document.createElement("script");
    script.id = "google-translate-script";
    script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    script.onerror = () => reject(new Error("Google Translate script failed"));
    document.body.appendChild(script);
  });
}
